import { parseCellId, type MapDef } from '@brig/rules-engine';
import manifest from '../assets/terrain/manifest.json';
import type { TerrainSpriteName, TerrainSprites } from './terrainSprites.ts';

/**
 * The painted props standing on the isometric terrain (see terrainSprites.ts): trees and
 * bushes over the islands, a village of houses with a bell tower and sometimes a chapel round
 * each fort, wooden piers running out into its port, an old watchtower on a few remote
 * islands, and rocks breaking the surf off the shores. They are painted once into the terrain
 * canvas, farthest first, and are seeded by the map, so a map always looks the same.
 */

/** Everything a prop needs to know about the ground, in cell units (u east, v south). */
export interface Ground {
  readonly seed: number;
  /** Land potential: above `shore` is land. */
  readonly potential: (u: number, v: number) => number;
  readonly shore: number;
  /** Height above the sea, in cells. */
  readonly elevation: (u: number, v: number) => number;
  readonly maxElevation: number;
  readonly fbm: (x: number, y: number, octaves: number) => number;
  /** A ground point (and a height above it, in cells) to canvas pixels. */
  readonly project: (u: number, v: number, z: number) => readonly [number, number];
  /** Canvas pixels per cell. */
  readonly scale: number;
  readonly sprites: TerrainSprites;
}

interface Prop {
  /** Canvas y of the prop's foot: props are painted in order of it, farthest first. */
  readonly depth: number;
  readonly draw: (ctx: CanvasRenderingContext2D) => void;
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The sheet's scale: its pixels per map cell, so every prop keeps its painted size. */
const SHEET_PX_PER_CELL = 560;
/** Houses in a village, its bell tower and chapel among them. */
const VILLAGE = 7;
/** Old watchtowers on remote islands, at most, and how far apart. */
const WATCHTOWERS = 4;
const WATCHTOWER_GAP = 7;

/**
 * Where a prop meets the ground, as a fraction of its image's height up from the bottom: a
 * house's front corner and steps reach below the middle of its footprint, a rock's water
 * spreads round it.
 */
const FOOT: Partial<Record<TerrainSpriteName, number>> = {
  'house-1': 0.1,
  'house-2': 0.12,
  'house-3': 0.14,
  'house-4': 0.1,
  'bell-tower': 0.1,
  chapel: 0.12,
  'fort-tower': 0.12,
  'rock-1': 0.22,
  'rock-2': 0.22,
  'rock-3': 0.22,
  'rock-4': 0.3,
  'rock-5': 0.3,
  'rock-6': 0.3,
  'pier-long': 0.35,
  'pier-short': 0.35,
};

const pick = <T>(rand: () => number, items: readonly T[]): T => items[Math.floor(rand() * items.length)] as T;

/**
 * The straight cliff pieces, with how much shore each covers in cells at its painted size: the
 * sheet paints them running east–west, their front edge across most of the image.
 */
const CLIFFS = (['cliff-1', 'cliff-2', 'cliff-3', 'cliff-4', 'cliff-5', 'cliff-6', 'cliff-7'] as const).map(
  (name) => ({
    name,
    height: manifest[name].height,
    run: (manifest[name].width / (SHEET_PX_PER_CELL * Math.SQRT1_2)) * 0.8,
  }),
);
/** Where a cliff piece's top front edge runs, as a fraction of its image's height from the top. */
const CLIFF_EDGE = 0.33;
/** How much of a cliff piece's image its face takes up, from the top edge down to the sea. */
const CLIFF_FACE = 0.52;
/** Shore tracing steps, in cells: along the shore and across it. */
const ALONG = 0.05;
const ACROSS = 0.02;

/**
 * The stretches of shore facing the camera, south or east, where land gives way to water: each
 * from one point along the shore to another, with where the shore runs across it in between.
 * A stretch ends where the shore turns sharply.
 */
function shoreRuns(g: Ground, map: MapDef, facing: 'south' | 'east') {
  const [lenAlong, lenAcross] = facing === 'south' ? [map.width, map.height] : [map.height, map.width];
  const land = (t: number, c: number) =>
    facing === 'south' ? g.potential(t, c) > g.shore : g.potential(c, t) > g.shore;
  type Run = { from: number; to: number; points: [number, number][] };
  const done: Run[] = [];
  let open: Run[] = [];
  for (let t = ALONG / 2; t < lenAlong; t += ALONG) {
    const next: Run[] = [];
    for (let c = ACROSS; c < lenAcross - ACROSS; c += ACROSS) {
      if (!land(t, c) || land(t, c + ACROSS)) continue;
      const edge = c + ACROSS / 2;
      const i = open.findIndex((r) => Math.abs((r.points.at(-1)?.[1] ?? -9) - edge) < 0.09);
      const run = i >= 0 ? open.splice(i, 1)[0] : undefined;
      if (run) {
        run.to = t;
        run.points.push([t, edge]);
        next.push(run);
      } else next.push({ from: t, to: t, points: [[t, edge]] });
    }
    done.push(...open);
    open = next;
  }
  done.push(...open);
  return done.map((r) => ({
    from: r.from,
    to: r.to + ALONG,
    at: (t: number) => r.points[Math.min(r.points.length - 1, Math.round((t - r.from) / ALONG))]?.[1] ?? 0,
  }));
}

export function drawProps(ctx: CanvasRenderingContext2D, map: MapDef, g: Ground): void {
  const rand = mulberry32(g.seed ^ 0x2545f491);
  const props: Prop[] = [];
  const place = (name: TerrainSpriteName, u: number, v: number, z: number, size = 1, mirror = false) => {
    const prop = spriteProp(g, name, u, v, z, size, mirror);
    if (prop) props.push(prop);
  };
  const forts = map.forts.flatMap((f) => {
    const p = parseCellId(f.cell);
    return p ? [{ x: p.x + 0.5, y: p.y + 0.5, ports: f.portCells.flatMap((c) => parseCellId(c) ?? []) }] : [];
  });
  const nearestFort = (u: number, v: number) =>
    Math.min(Infinity, ...forts.map((f) => Math.hypot(u - f.x, v - f.y)));
  const isLand = (u: number, v: number, margin: number) => g.potential(u, v) > g.shore + margin;

  // -- villages: houses round each fort, a bell tower and maybe a chapel among them --
  const built: { u: number; v: number; gap: number }[] = [];
  const roomFor = (u: number, v: number, gap: number) =>
    built.every((b) => Math.hypot(b.u - u, b.v - v) > Math.max(gap, b.gap));
  for (const f of forts) {
    const spots: { u: number; v: number; d: number }[] = [];
    for (let v = f.y - 1.6; v <= f.y + 1.6; v += 0.24) {
      for (let u = f.x - 1.6; u <= f.x + 1.6; u += 0.24) {
        const su = u + (rand() - 0.5) * 0.12;
        const sv = v + (rand() - 0.5) * 0.12;
        const d = Math.hypot(su - f.x, sv - f.y);
        // Clear of the fort's star and flag, on land a little back from the cliff.
        if (d < 0.5 || d > 1.6 || !isLand(su, sv, 0.08)) continue;
        spots.push({ u: su, v: sv, d });
      }
    }
    spots.sort((a, b) => a.d - b.d);
    const village: TerrainSpriteName[] = ['bell-tower'];
    if (rand() < 0.5) village.push('chapel');
    let houses = 0;
    for (const s of spots) {
      if (houses >= VILLAGE || rand() > 1.4 - s.d / 1.8 || !roomFor(s.u, s.v, 0.3)) continue;
      houses++;
      const name = village.shift() ?? pick(rand, ['house-1', 'house-2', 'house-3', 'house-4'] as const);
      built.push({ u: s.u, v: s.v, gap: 0.3 });
      place(name, s.u, s.v, g.elevation(s.u, s.v), 0.9 + rand() * 0.15);
    }
    // -- piers: one into each port cell beside the fort, off to the side of the anchorage --
    for (const port of f.ports) {
      const dx = port.x + 0.5 - f.x;
      const dy = port.y + 0.5 - f.y;
      if (Math.abs(dx) + Math.abs(dy) !== 1) continue;
      const side = rand() < 0.5 ? -1 : 1;
      // The sheet's piers run east–west; one running north–south is the mirror image.
      place(
        rand() < 0.5 ? 'pier-short' : 'pier-long',
        f.x + dx * 0.62 + dy * 0.3 * side,
        f.y + dy * 0.62 + dx * 0.3 * side,
        0,
        0.75,
        dy !== 0,
      );
    }
  }

  // -- watchtowers: an old tower on the high ground of a few islands far from any fort --
  const towers: { u: number; v: number }[] = [];
  for (let v = 0.5; v < map.height && towers.length < WATCHTOWERS; v += 0.5) {
    for (let u = 0.5; u < map.width && towers.length < WATCHTOWERS; u += 0.5) {
      if (!isLand(u, v, 0.15) || nearestFort(u, v) < 3) continue;
      if (towers.some((t) => Math.hypot(t.u - u, t.v - v) < WATCHTOWER_GAP)) continue;
      if (g.fbm(u * 0.9 + 41, v * 0.9 - 13, 2) < 0.55) continue;
      towers.push({ u, v });
      built.push({ u, v, gap: 0.35 });
      place('fort-tower', u, v, g.elevation(u, v), 1);
    }
  }

  // -- trees: woods and copses over the islands, thinning out by the villages --
  for (let v = 0; v < map.height; v += 0.16) {
    for (let u = 0; u < map.width; u += 0.16) {
      const tu = u + (rand() - 0.5) * 0.13;
      const tv = v + (rand() - 0.5) * 0.13;
      if (!isLand(tu, tv, 0.06)) continue;
      if (g.elevation(tu, tv) / g.maxElevation > 0.62) continue;
      const village = nearestFort(tu, tv);
      if (village < 0.5 || !roomFor(tu, tv, 0.18)) continue;
      const woods =
        g.fbm(tu * 2.2 + 3, tv * 2.2 - 7, 3) + rand() * 0.3 - 0.25 * (1 - Math.min(1, village / 1.6));
      if (woods < 0.6) continue;
      const kind = g.fbm(tu * 6 - 1, tv * 6 + 2, 2);
      const name =
        kind > 0.6
          ? pick(rand, ['cypress-1', 'cypress-2', 'cypress-3'] as const)
          : kind < 0.38
            ? pick(rand, ['bush-1', 'bush-2'] as const)
            : pick(rand, ['tree-1', 'tree-2', 'tree-3'] as const);
      place(name, tu, tv, g.elevation(tu, tv), 0.75 + rand() * 0.3);
    }
  }

  // -- cliffs: painted cliff faces along the shores that face the camera --
  for (const facing of ['south', 'east'] as const) {
    for (const run of shoreRuns(g, map, facing)) {
      // The cliff's top, a little inland of the shore line at t along it.
      const topAt = (t: number) => {
        const across = run.at(t);
        return facing === 'south' ? g.elevation(t, across - 0.06) : g.elevation(across - 0.06, t);
      };
      let t = run.from;
      while (run.to - t > 0.18) {
        const left = run.to - t + 0.08;
        // Each piece scaled so its painted face reaches from the top down to the sea here.
        const sized = CLIFFS.map((c) => {
          const size = Math.min(
            2.6,
            Math.max(0.5, (1.1 * topAt(t + 0.2) * SHEET_PX_PER_CELL) / (CLIFF_FACE * c.height)),
          );
          return { ...c, size, length: c.run * size };
        });
        const fits = sized.filter((c) => c.length <= left);
        const shortest = sized.reduce((a, b) => (b.length < a.length ? b : a));
        const piece = fits.length
          ? pick(rand, fits)
          : { ...shortest, size: shortest.size * (left / shortest.length) };
        const length = piece.run * piece.size;
        const mid = t + length / 2;
        const across = run.at(mid);
        const [u, v] = facing === 'south' ? [mid, across] : [across, mid];
        const prop = cliffProp(g, piece.name, u, v, topAt(mid), piece.size, facing === 'east');
        if (prop) props.push(prop);
        t += length;
      }
    }
  }

  // -- rocks: in the surf just off the shores of coast cells, mostly small --
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (map.rows[y]?.[x] !== ',') continue;
      for (let i = 0; i < 4; i++) {
        const u = x + 0.1 + rand() * 0.8;
        const v = y + 0.1 + rand() * 0.8;
        const p = g.potential(u, v);
        if (p > g.shore - 0.03 || p < g.shore - 0.12 || rand() > 0.3) continue;
        const name =
          rand() < 0.8
            ? pick(rand, ['rock-4', 'rock-5', 'rock-6'] as const)
            : pick(rand, ['rock-1', 'rock-2', 'rock-3'] as const);
        place(name, u, v, 0, 0.6 + rand() * 0.3);
      }
    }
  }

  props.sort((a, b) => a.depth - b.depth);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  for (const p of props) p.draw(ctx);
}

/** A cliff piece whose top front edge runs through (u, v) at height z, along u (or v, mirrored). */
function cliffProp(
  g: Ground,
  name: TerrainSpriteName,
  u: number,
  v: number,
  z: number,
  size: number,
  mirror: boolean,
): Prop | null {
  const img = g.sprites.get(name);
  if (!img) return null;
  const { width, height } = manifest[name];
  const k = (g.scale / SHEET_PX_PER_CELL) * size;
  const w = width * k;
  const h = height * k;
  const [x, y] = g.project(u, v, z);
  const top = y - h * CLIFF_EDGE;
  return {
    depth: y,
    draw: (ctx) => {
      ctx.save();
      ctx.translate(x, 0);
      if (mirror) ctx.scale(-1, 1);
      ctx.drawImage(img, -w / 2, top, w, h);
      ctx.restore();
    },
  };
}

/** A sprite standing with its foot at (u, v) on ground z cells high, `size` times its painted size. */
function spriteProp(
  g: Ground,
  name: TerrainSpriteName,
  u: number,
  v: number,
  z: number,
  size: number,
  mirror: boolean,
): Prop | null {
  const img = g.sprites.get(name);
  if (!img) return null;
  const { width, height } = manifest[name];
  const k = (g.scale / SHEET_PX_PER_CELL) * size;
  const w = width * k;
  const h = height * k;
  const [x, y] = g.project(u, v, z);
  const top = y - h * (1 - (FOOT[name] ?? 0.04));
  return {
    depth: y,
    draw: (ctx) => {
      if (!mirror) {
        ctx.drawImage(img, x - w / 2, top, w, h);
        return;
      }
      ctx.save();
      ctx.translate(x, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -w / 2, top, w, h);
      ctx.restore();
    },
  };
}
